import React, { createContext, useContext } from 'react';

interface PeopleMagicDesignControllerContextData {
  homepage?: {
    heroMainHeadline: string | boolean;
    heroYellowHeadline?: string;
    heroSubHeadline?: string;
    hasPeopleMagicAdvertisementMovedToTop?: boolean;
    heroCustomMaxWidth?: string;
    isLogoClickable?: boolean;
    peopleMagicProfitAnnouncementLinkSlug?: string;
    minimalHeroHeight?: string;
    hideHeaderInternalLinks?: boolean;
  };
  peopleMagicProfit?: {
    heroMainHeadline: string;
    heroYellowHeadline?: string;
    heroSubHeadline?: string;
    firstFormSection?: {
      headline?: string;
    };
    secondFormSection?: {
      headline?: string;
      subHeadline?: string;
      items?: string[];
    };
  };
}

const PeopleMagicDesignControllerContext = createContext<PeopleMagicDesignControllerContextData>(
  {} as PeopleMagicDesignControllerContextData
);

interface PeopleMagicDesignControllerProviderProps extends PeopleMagicDesignControllerContextData {
  children: React.ReactNode;
}

const PeopleMagicDesignControllerProvider: React.FC<PeopleMagicDesignControllerProviderProps> = ({
  homepage,
  peopleMagicProfit,
  children,
}: PeopleMagicDesignControllerProviderProps) => {
  return (
    <PeopleMagicDesignControllerContext.Provider value={{ homepage, peopleMagicProfit }}>
      {children}
    </PeopleMagicDesignControllerContext.Provider>
  );
};

const usePeopleMagicDesignController = (): PeopleMagicDesignControllerContextData =>
  useContext(PeopleMagicDesignControllerContext);

const PaidAdsPageData: Record<string, PeopleMagicDesignControllerContextData> = {
  coursesandcommunity: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: 'Everything in one place to run',
      heroYellowHeadline: 'your courses and a community',
      heroSubHeadline:
        'What’s possible when you bring your courses and community together on a Mighty Network? You build a community you can be proud of.',
      hasPeopleMagicAdvertisementMovedToTop: true,
      heroCustomMaxWidth: '1400px',
      isLogoClickable: false,
    },
  },
  growing: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: 'Stop grinding.',
      heroYellowHeadline: 'Start growing.',
      heroSubHeadline:
        'What’s possible when a paid membership, online courses, challenges, or events are powered by AI? Your community essentially runs itself.',
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
      peopleMagicProfitAnnouncementLinkSlug: '/peoplemagic/growing',
    },
    peopleMagicProfit: {
      heroMainHeadline: 'Stop grinding.',
      heroYellowHeadline: 'Start growing.',
      heroSubHeadline:
        'Discover the ONLY things you need to build a $1M community in this FREE Masterclass from Mighty Networks',
    },
  },
  creators: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: 'Built by the world’s most successful creators,',
      heroYellowHeadline: 'for ALL creators to succeed',
      heroSubHeadline: `What if there was a community platform co-created with some of the most successful creators and brands in the world? That’s a Mighty Network. And the results are extraordinary.`,
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
      peopleMagicProfitAnnouncementLinkSlug: '/peoplemagic/creators',
      heroCustomMaxWidth: '1400px',
      minimalHeroHeight: '175vh',
    },
    peopleMagicProfit: {
      heroMainHeadline: 'This is strategy is quietly making creators millions',
      heroSubHeadline:
        'What if you could get the EXACT strategy used by the most successful creators in the world to build a business that runs itself? You’ve come to the right place.',
    },
  },
  onemillion: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: '',
      heroYellowHeadline: 'Build a $1M Community with AI',
      heroSubHeadline: `Mighty Networks hosts more $1M communities than any other platform. Our secret? Combining powerful features with AI, so your community runs itself.`,
      heroCustomMaxWidth: '1400px',
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
      peopleMagicProfitAnnouncementLinkSlug: '/peoplemagic/onemillion',
    },
    peopleMagicProfit: {
      heroMainHeadline: 'Build a $1M Community with AI',
      heroSubHeadline:
        'What if you could get the EXACT strategy used by the most successful creators in the world to build a business that runs itself? You’ve come to the right place.',
    },
  },
  fromzero: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: false,
      heroYellowHeadline: 'From $0 to $182,000/month',
      heroSubHeadline: `People launching community, courses, challenges, and events on Mighty Networks are seeing extraordinary results.\nThe secret?\nPowerful features and AI, so your community runs itself.`,
      heroCustomMaxWidth: '1400px',
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
      peopleMagicProfitAnnouncementLinkSlug: '/peoplemagic/fromzero',
    },
    peopleMagicProfit: {
      heroMainHeadline: 'From $0 to $182,000/month',
      heroSubHeadline: `What if you could get the EXACT strategy used by the most successful creators in the world to build a business that runs itself? You’ve come to the right place.`,
    },
  },
  atm: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: '“Uh...I think we just built an ATM machine for creators.”',
      heroYellowHeadline: '',
      heroSubHeadline: `People launching community, courses, challenges, and events on Mighty Networks are generating MILLIONS in profit. The secret? Creating connections between your members using AI we call people magic.`,
      heroCustomMaxWidth: '1400px',
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
      peopleMagicProfitAnnouncementLinkSlug: '/peoplemagic/atm',
      minimalHeroHeight: '175vh',
    },
    peopleMagicProfit: {
      heroMainHeadline: 'Strategy to create a virtual ATM machine for your business',
      heroSubHeadline: `What if you could get the EXACT strategy used by the most successful creators in the world to build a business that runs itself?\nYou’ve come to the right place.`,
    },
  },
  introduces: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: 'The only community platform',
      heroYellowHeadline: 'that introduces your members',
      heroSubHeadline: `What’s possible when you introduce your members to each other? Your courses, community, challenges, and events run themselves.`,
      heroCustomMaxWidth: '1400px',
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
    },
  },
  offer: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: `What’s better than\n3 months free?`,
      heroYellowHeadline: '',
      heroSubHeadline:
        'Getting it on the ONLY course and community platform that connects your members with AI for extraordinary engagement.',
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
    },
  },
  mostmillions: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: 'The most $1M communities of any platform',
      heroYellowHeadline: '',
      heroSubHeadline:
        'Bring your courses, community, challenges, and events together on the ONLY platform that uses AI to introduce your members.',
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
      heroCustomMaxWidth: '1100px',
    },
  },
  copycats: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: 'You’d probably want to copy Mighty Networks too',
      heroYellowHeadline: '',
      heroSubHeadline: `Mighty Networks is the ONLY course and community platform that uses patent-pending AI to intelligently connect your members to each other\nfor extraordinary engagement and 99% profit margin.`,
      hasPeopleMagicAdvertisementMovedToTop: true,
      isLogoClickable: false,
      heroCustomMaxWidth: '1100px',
    },
  },
};

const FitnessPageData: Record<string, PeopleMagicDesignControllerContextData> = {
  fitnessprofessionals: {
    homepage: {
      hideHeaderInternalLinks: true,
      heroMainHeadline: 'The #1 community platform',
      heroYellowHeadline: 'for fitness professionals',
      heroSubHeadline:
        'What’s possible when a paid membership, training programs, challenges, or events are powered by AI? Your fitness community essentially runs itself.',
      hasPeopleMagicAdvertisementMovedToTop: false,
      heroCustomMaxWidth: '1100px',
      isLogoClickable: false,
    },
  },
};

export { usePeopleMagicDesignController, PeopleMagicDesignControllerProvider, PaidAdsPageData, FitnessPageData };
